import { memo, useContext, useMemo } from 'react';
import { ConvertType, useFormatNumber } from '../../../hooks/useFormatNumber';
import { T } from '../../../translation/src';
import { PropertiesSection, Property } from './PropertiesSection';
import { useMeasurements } from '../../../hooks/useMeasurements';
import { RendererContext } from '../../../contexts/RendererContext';
import { PropertyPanel } from '../../Properties/components/PropertyPanel';
import { AnnotationType } from '../../Properties/types';
import { getAreaPointsProperty } from '@pointorama/potree-shared';
import { usePropertyValue } from '../../Properties/reactive/usePropertyValue';

interface OpeningPropertiesProps {
  projectId: string;
  identifier: string;
}

const OpeningProperties_: React.FC2<OpeningPropertiesProps> = ({ projectId, identifier }) => {
  const { formatNumber } = useFormatNumber();
  const { perimeterMeasurement } = useMeasurements();

  const renderContext = useContext(RendererContext);
  const viewer = renderContext.viewer;
  // Openings share the AreaAnnotation geometry path, so the runtime
  // measure lives in `viewer.scene.measurements` just like an AREA.
  const polygonObject = viewer?.scene.measurements.find((m) => m.identifier === identifier);
  const area = polygonObject?.getArea() || 0;
  const planar = polygonObject?.isPlanar() || false;

  const points = usePropertyValue(getAreaPointsProperty(identifier));
  const perimeter = useMemo(() => {
    return perimeterMeasurement(points.map((p) => p.position));
  }, [points, perimeterMeasurement]);

  return (
    <div className="flex flex-col space-y-4 divide-y divide-gray-800">
      <PropertiesSection title={<T _str="measurements" swc />}>
        <Property title={planar ? <T _str="area (3D)" swc /> : <T _str="area (2D)" swc />}>{formatNumber(area, { convertType: ConvertType.AREA, addUnit: true })}</Property>
        <Property title={<T _str="perimeter" swc />}>{formatNumber(perimeter, { convertType: ConvertType.DISTANCE, addUnit: true })}</Property>
      </PropertiesSection>
      <PropertyPanel projectId={projectId} annotationTypes={[AnnotationType.OPENING]} objectIdentifiers={[identifier]} />
    </div>
  );
};

export const OpeningProperties = memo(OpeningProperties_);
